'use client';

import { Skeleton } from '@/components/ui/skeleton';
import { useStatistics } from '@/lib/services/statistics/service';
import { formatNumber } from '@/lib/utils/format-number';
import { LayersIcon, SparklesIcon } from 'lucide-react';
import type { ReactElement } from 'react';

export default function Statistics(): ReactElement {
  const { statistics } = useStatistics();

  if (!statistics.data)
    return (
      <div className="w-full flex items-center justify-center gap-3 sm:gap-4">
        <Skeleton className="h-20 w-36 sm:h-24 sm:w-44 rounded-2xl" />
        <Skeleton className="h-20 w-36 sm:h-24 sm:w-44 rounded-2xl" />
      </div>
    );

  return (
    <div className="flex flex-row items-center justify-center gap-3 sm:gap-4">
      <div className="group relative flex flex-col items-center justify-center gap-1.5 rounded-2xl border border-border bg-card px-6 py-4 sm:px-8 sm:py-5 shadow-sm transition-shadow hover:shadow-md overflow-hidden w-36 sm:w-44">
        <div className="absolute inset-0 bg-linear-to-br from-emerald-500/5 via-cyan-500/5 to-transparent pointer-events-none" />
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <SparklesIcon className="w-3.5 h-3.5 shrink-0 text-emerald-500 dark:text-emerald-400" />
          <span className="text-xs font-medium uppercase tracking-wider">Generations</span>
        </div>
        <p className="text-2xl sm:text-3xl font-bold tabular-nums bg-linear-to-r from-emerald-500 via-cyan-500 to-blue-600 bg-clip-text text-transparent dark:from-emerald-300 dark:via-cyan-300 dark:to-blue-400">
          {formatNumber(statistics.data.result.generations)}
        </p>
      </div>
      <div className="group relative flex flex-col items-center justify-center gap-1.5 rounded-2xl border border-border bg-card px-6 py-4 sm:px-8 sm:py-5 shadow-sm transition-shadow hover:shadow-md overflow-hidden w-36 sm:w-44">
        <div className="absolute inset-0 bg-linear-to-br from-blue-500/5 via-cyan-500/5 to-transparent pointer-events-none" />
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <LayersIcon className="w-3.5 h-3.5 shrink-0 text-blue-500 dark:text-blue-400" />
          <span className="text-xs font-medium uppercase tracking-wider">Concepts</span>
        </div>
        <p className="text-2xl sm:text-3xl font-bold tabular-nums bg-linear-to-r from-cyan-500 via-blue-500 to-blue-600 bg-clip-text text-transparent dark:from-cyan-300 dark:via-blue-300 dark:to-blue-400">
          {formatNumber(statistics.data.result.concepts)}
        </p>
      </div>
    </div>
  );
}
